// Area: records (owner record-ops in wave 1, record-runtime in wave 2; the bulk contract belongs to
// views-filters-bulk in wave 2). One record mutation contract (plan §8.8, §7.3): every write takes
// an expected version and returns an undo token; one bulk API; undo; history restore; job progress.
import { z } from 'zod';
import {
  BulkMutationInput,
  BulkMutationResult,
  CreateLinkInput,
  CreateRecordInput,
  FilterNodeSchema,
  QueryResponse,
  RecordChangeEntry,
  RecordDTO,
  RecordDeleteResult,
  RecordJobDTO,
  RecordLink,
  RecordRef,
  RecordWriteResult,
  LinkWriteResult,
  RestoreRecordInput,
  RestoreValueInput,
  UndoResult,
  UndoToken,
  UnlinkResult,
  UpdateLinkInput,
  UpdateRecordInput,
} from '@dataroom/core';
import { BulkActionInput, BulkActionResult } from '@dataroom/core/views';
import { defineContract } from '../define';
import { q } from '../query';

export {
  BulkMutationInput,
  BulkMutationResult,
  LinkWriteResult,
  RecordChangeEntry,
  RecordDeleteResult,
  RecordJobDTO,
  RecordWriteResult,
  UndoResult,
  UndoToken,
  UnlinkResult,
};

const ObjectKey = z.string().min(1).max(63);
const Key = z.object({ key: ObjectKey });
const KeyId = z.object({ key: ObjectKey, id: z.uuid() });
const KeyIdRel = z.object({ key: ObjectKey, id: z.uuid(), relation: z.string().min(1).max(63) });
const KeyIdRelLink = z.object({
  key: ObjectKey,
  id: z.uuid(),
  relation: z.string().min(1).max(63),
  linkId: z.uuid(),
});
/** `?expectedVersion=3` on writes without a body (delete, restore, unlink). */
const VersionQuery = z.object({ expectedVersion: q.int().positive().optional() });

/** `name:asc` / `amount:desc` (direction defaults to asc). */
export const SortToken = z
  .string()
  .max(120)
  .regex(/^[a-z_][a-z0-9_.]*(:(asc|desc))?$/, { error: 'Use field:asc or field:desc' });

export const ListRecordsQuery = z.object({
  limit: q.int().min(1).max(500).default(50),
  cursor: z.string().max(4000).optional(),
  q: z.string().max(200).optional(),
  count: z.enum(['none', 'estimate', 'exact']).default('estimate'),
  sort: q.list(SortToken, { max: 20 }).optional(),
  fields: q.list(z.string().min(1).max(300), { max: 500 }).optional(),
  filter: q.json(FilterNodeSchema).optional(),
});

/** Parse sort tokens into the query request's sort specs. */
export function parseSortTokens(
  tokens: readonly string[] | undefined,
): Array<{ field: string; dir: 'asc' | 'desc' }> {
  return (tokens ?? []).map((token) => {
    const [field, dir] = token.split(':');
    return { field: field!, dir: dir === 'desc' ? 'desc' : 'asc' };
  });
}

export const listRecordsContract = defineContract({
  method: 'GET',
  path: '/objects/:key/records',
  params: Key,
  query: ListRecordsQuery,
  response: QueryResponse,
  summary: 'List records (keyset pagination); same semantics as POST /objects/:key/query',
});

export const createRecordContract = defineContract({
  method: 'POST',
  path: '/objects/:key/records',
  params: Key,
  body: CreateRecordInput,
  response: RecordWriteResult,
  status: 201,
  summary: 'Create a record; returns it with an undo token (undo deletes it)',
});

export const getRecordContract = defineContract({
  method: 'GET',
  path: '/objects/:key/records/:id',
  params: KeyId,
  query: z.object({ include: z.string().max(100).optional() }),
  response: RecordDTO,
  summary: 'One record with all relation values; `include=sources` adds provenance per field',
});

export const updateRecordContract = defineContract({
  method: 'PATCH',
  path: '/objects/:key/records/:id',
  params: KeyId,
  body: UpdateRecordInput,
  response: RecordWriteResult,
  summary:
    'Update fields (stage changes too); `expectedVersion` gives field-level 409 conflicts; returns an undo token',
  description:
    'With `expectedVersion`, the write fails with `409 version_conflict` (details: current record, changed and conflicting fields, last change) only when a field it writes changed since that version; other changes are merged. The response carries `undo` when anything changed.',
});

export const deleteRecordContract = defineContract({
  method: 'DELETE',
  path: '/objects/:key/records/:id',
  params: KeyId,
  query: VersionQuery,
  response: RecordDeleteResult,
  summary: 'Soft delete (cascading); returns the deleted ids and an undo token',
});

export const restoreRecordContract = defineContract({
  method: 'POST',
  path: '/objects/:key/records/:id/restore',
  params: KeyId,
  query: VersionQuery,
  response: RecordWriteResult,
  summary: 'Restore a soft-deleted record; returns it with an undo token',
});

export const recordChangesContract = defineContract({
  method: 'GET',
  path: '/objects/:key/records/:id/changes',
  params: KeyId,
  query: z.object({ limit: q.int().min(1).max(200).default(50), before: z.uuid().optional() }),
  response: z.array(RecordChangeEntry),
  summary: 'Change history (record_changes), newest first, with operation and origin',
});

export const restoreValueContract = defineContract({
  method: 'POST',
  path: '/objects/:key/records/:id/changes/:changeId/restore',
  params: z.object({ key: ObjectKey, id: z.uuid(), changeId: z.uuid() }),
  body: RestoreValueInput,
  response: RecordWriteResult,
  summary: '"Restore this value": write a field value from a history entry as a new, undoable change',
});

export const restoreRecordToContract = defineContract({
  method: 'POST',
  path: '/objects/:key/records/:id/history/restore',
  params: KeyId,
  body: RestoreRecordInput,
  response: RecordWriteResult,
  summary: '"Restore record to this point": every field as of an instant or a change, as one undoable change',
});

export const listLinksContract = defineContract({
  method: 'GET',
  path: '/objects/:key/records/:id/links/:relation',
  params: KeyIdRel,
  query: z.object({ limit: q.int().min(1).max(1000).default(200) }),
  response: z.array(RecordLink),
  summary: 'Linked records of a relation field, with link field values',
});

export const addLinkContract = defineContract({
  method: 'POST',
  path: '/objects/:key/records/:id/links/:relation',
  params: KeyIdRel,
  body: CreateLinkInput,
  response: LinkWriteResult,
  status: 201,
  summary: 'Link a record through a relation field; returns the link with an undo token',
});

export const updateLinkContract = defineContract({
  method: 'PATCH',
  path: '/objects/:key/records/:id/links/:relation/:linkId',
  params: KeyIdRelLink,
  body: UpdateLinkInput,
  response: RecordLink,
  summary: 'Change link field values',
});

export const removeLinkContract = defineContract({
  method: 'DELETE',
  path: '/objects/:key/records/:id/links/:relation/:linkId',
  params: KeyIdRelLink,
  query: VersionQuery,
  response: UnlinkResult,
  summary: 'Unlink; returns an undo token (undo re-creates the link with its link field values)',
});

export const recordRefsContract = defineContract({
  method: 'GET',
  path: '/records/refs',
  query: z.object({ ids: q.list(z.uuid(), { max: 200 }) }),
  response: z.array(RecordRef),
  summary: 'Resolve record ids (any object) to { id, objectKey, title }',
});

/** The bulk bar's earlier actions (assign, add to list, enrich, run workflow) stay accepted. */
export const LegacyBulkActionResult = BulkActionResult.extend({ undo: UndoToken.optional() });

export const bulkContract = defineContract({
  method: 'POST',
  path: '/objects/:key/bulk',
  params: Key,
  body: z.union([BulkMutationInput, BulkActionInput]),
  response: z.union([BulkMutationResult, LegacyBulkActionResult]),
  /** Reading is enough to ask; every record's write is checked by the policy (per-record 403). */
  permission: 'records.read',
  summary: 'Bulk mutation: set, clear, add, remove, link, unlink, delete, restore or paste cells',
  description:
    'Select records by `ids` or by `filter` + `q` + `excludeIds`. Per-record results (a failing record never blocks the others), `dryRun`, one undo token for the whole operation; above 1,000 records a `jobId` to follow with GET /jobs/:id. The legacy `{ action: "update" | "add_to_list" | "enrich" | "run_workflow", ids }` body is still accepted.',
});

export const undoContract = defineContract({
  method: 'POST',
  path: '/undo/:token',
  params: z.object({ token: z.uuid() }),
  response: UndoResult,
  summary: 'Revert a record operation with its undo token (field-level); returns a redo token',
  description:
    'Only the member who made the change can use its token, for 10 minutes (403 otherwise, 410 when expired or used). Fields changed by someone since are left alone and listed in `skippedFields`.',
});

export const recordJobContract = defineContract({
  method: 'GET',
  path: '/jobs/:id',
  params: z.object({ id: z.uuid() }),
  response: RecordJobDTO,
  summary: 'Progress of a background record operation (bulk write or large undo)',
});
